// src/components/common/Header.jsx
import React, { useState, useEffect } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import useAuthStore from '../../store/authStore';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaCog,
  FaSignOutAlt,
  FaUser,
  FaTimes,
  FaBars,
  FaRocket,
  FaCalendarAlt,
  FaBell,
  FaChevronDown,
  FaUserShield,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import { get } from '../../helpers/apiHelper';

// Archetype System
import { useArchetype } from '../../contexts/ArchetypeContext';
import { useAdmin } from '../../contexts/AdminContext';

function Header({ toggleSidebar }) {
  const { user, logout } = useAuthStore();
  const { archetype } = useArchetype();
  const { isAdmin, isAdminMode, toggleAdminMode } = useAdmin();
  const navigate = useNavigate();
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      const fetchUserData = async () => {
        try {
          const response = await get(`users/${user.userId}`);
          if (response) {
            const userData = response;
            setIsSubscribed(userData.hasPaid);
            if (!userData.hasPaid) {
              const trialEndDate = new Date(userData.trialStarted);
              trialEndDate.setDate(trialEndDate.getDate() + 30);
              const daysLeft = differenceInCalendarDays(trialEndDate, new Date());
              setTrialDaysLeft(daysLeft);
            }
          } else {
            console.error('User data not found:', response);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      };
      fetchUserData();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNavigateToSubscribe = () => {
    navigate('/subscribe');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  return (
    <header className="bg-white shadow-lg border-b border-gray-100 relative z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/logos/logopng.png"
                alt="PetitPrime Logo"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                PetitPrime
              </span>
            </Link>

            {/* Archetype Badge - OCULTO (solo para uso interno) */}
            {/* {user && archetype && (
              <div className="ml-4">
                <ArchetypeBadge archetype={archetype} />
              </div>
            )} */}
          </div>

          {/* Navegación de escritorio */}
          <nav className="hidden lg:flex items-center space-x-8">
            {user ? (
              <>
                {/* Admin Mode Toggle */}
                {isAdmin && (
                  <button
                    onClick={toggleAdminMode}
                    className="flex items-center px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <FaUserShield className={`mr-2 ${isAdminMode ? 'text-purple-600' : 'text-gray-600'}`} />
                    {isAdminMode ? (
                      <>
                        <span className="mr-2">Modo Admin</span>
                        <FaToggleOn className="text-purple-600 text-xl" />
                      </>
                    ) : (
                      <>
                        <span className="mr-2">Modo Usuario</span>
                        <FaToggleOff className="text-gray-400 text-xl" />
                      </>
                    )}
                  </button>
                )}

                {/* Trial Banner */}
                {!isSubscribed && trialDaysLeft !== null && (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      <FaBell className="mr-1" />
                      {trialDaysLeft > 0 ? `${trialDaysLeft} días restantes` : 'Trial expirado'}
                    </div>
                  </div>
                )}

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                      <FaUser className="text-white text-sm" />
                    </div>
                    <span className="text-gray-700 font-medium">{user.email}</span>
                    <FaChevronDown className="text-gray-500 text-xs" />
                  </button>

                  {/* Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <FaCog className="mr-3 text-gray-500" />
                        Configuración
                      </Link>
                      {!isSubscribed && (
                        <button
                          onClick={() => { handleNavigateToSubscribe(); setUserMenuOpen(false); }}
                          className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <FaCalendarAlt className="mr-3 text-gray-500" />
                          Suscribirse
                        </button>
                      )}
                      <hr className="my-2" />
                      <button
                        onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                        className="w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <FaSignOutAlt className="mr-3" />
                        Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>

                {/* Subscribe Button */}
                {!isSubscribed && (
                  <button
                    onClick={handleNavigateToSubscribe}
                    className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-blue-900 font-bold py-2 px-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <FaRocket className="mr-2" />
                    Suscribirse
                  </button>
                )}
              </>
            ) : (
              <>
                <Link to="/features" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                  Características
                </Link>
                <Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                  Acerca de
                </Link>
                <Link to="/blog" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                  Blog
                </Link>
                {/* <Link to="/pricing" className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200">
                  Precios
                </Link> */}
                <button
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-2 px-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Iniciar Sesión
                </button>
              </>
            )}
          </nav>

          {/* Botón de menú móvil */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle navigation"
            >
              {mobileMenuOpen ? (
                <FaTimes className="text-2xl text-gray-700" />
              ) : (
                <FaBars className="text-2xl text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Menú móvil */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100">
            {user ? (
              <div className="flex flex-col space-y-3 pt-4">
                {/* Admin Mode Toggle */}
                {isAdmin && (
                  <button
                    onClick={() => { toggleAdminMode(); setMobileMenuOpen(false); }}
                    className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                  >
                    <div className="flex items-center">
                      <FaUserShield className={`mr-3 ${isAdminMode ? 'text-purple-600' : 'text-gray-600'}`} />
                      {isAdminMode ? 'Modo Admin' : 'Modo Usuario'}
                    </div>
                    {isAdminMode ? (
                      <FaToggleOn className="text-purple-600 text-xl" />
                    ) : (
                      <FaToggleOff className="text-gray-400 text-xl" />
                    )}
                  </button>
                )}

                {/* Trial Info */}
                {!isSubscribed && trialDaysLeft !== null && (
                  <div className="flex items-center justify-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-xl text-sm font-medium">
                    <FaBell className="mr-2" />
                    {trialDaysLeft > 0 ? `${trialDaysLeft} días restantes` : 'Trial expirado'}
                  </div>
                )}

                {/* User Info */}
                <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                    <FaUser className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.email}</p>
                    <p className="text-sm text-gray-500">Usuario</p>
                  </div>
                </div>

                {/* Menu Items */}
                <Link
                  onClick={() => setMobileMenuOpen(false)}
                  to="/settings"
                  className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                >
                  <FaCog className="mr-3 text-gray-500" />
                  Configuración
                </Link>

                {!isSubscribed && (
                  <button
                    onClick={() => { handleNavigateToSubscribe(); setMobileMenuOpen(false); }}
                    className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                  >
                    <FaCalendarAlt className="mr-3 text-gray-500" />
                    Suscribirse
                  </button>
                )}

                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200"
                >
                  <FaSignOutAlt className="mr-3" />
                  Cerrar Sesión
                </button>
              </div>
            ) : (
              <div className="flex flex-col space-y-3 pt-4">
                <Link
                  onClick={() => setMobileMenuOpen(false)}
                  to="/features"
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                >
                  Características
                </Link>
                <Link
                  onClick={() => setMobileMenuOpen(false)}
                  to="/about"
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                >
                  Acerca de
                </Link>
                <Link
                  onClick={() => setMobileMenuOpen(false)}
                  to="/blog"
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                >
                  Blog
                </Link>
                {/* <Link
                  onClick={() => setMobileMenuOpen(false)}
                  to="/pricing"
                  className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                >
                  Precios
                </Link> */}
                <button
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Iniciar Sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
